import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getCurrencies, deleteCurrency, setBaseCurrency } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import SearchInput from "../components/UI/SearchInput";
import ConfirmModal from "../components/UI/ConfirmModal";
import { notify } from "../utils/notify";
import DataTable from "../components/UI/DataTable";
import PageHeader from "../components/UI/PageHeader";
import { useTranslation } from "../context/I18nContext";

export default function Currencies() {
  const { t } = useTranslation("currencies");
  const { t: tCommon } = useTranslation("common");
  const [currencies, setCurrencies] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(8);
  const [sortConfig, setSortConfig] = useState({ key: "is_base", direction: "desc" });
  const [modalConfig, setModalConfig] = useState({ isOpen: false, id: null });
  const [status, setStatus] = useState("idle");
  const [settingBaseId, setSettingBaseId] = useState(null);

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchCurrencies = async () => {
    try {
      const data = await getCurrencies(token);
      setCurrencies(data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchCurrencies(); }, []);

  const sortedAndFiltered = useMemo(() => {
    let result = [...currencies].filter((c) =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.code?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    result.sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();

      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [currencies, searchTerm, sortConfig]);

  const currentItems = sortedAndFiltered.slice((currentPage - 1) * perPage, currentPage * perPage);
  const totalPages = Math.ceil(sortedAndFiltered.length / perPage) || 1;

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const handleDeleteClick = (id) => setModalConfig({ isOpen: true, id });

  const confirmDelete = async () => {
    try {
      setStatus("loading");
      await deleteCurrency(token, modalConfig.id);
      setStatus("success");
      setTimeout(() => {
        setModalConfig({ isOpen: false, id: null });
        setStatus("idle");
      }, 1200);
      notify.success(t("notify_deleted", "Currency deleted successfully"));
      fetchCurrencies();
    } catch (err) {
      setStatus("error");
      notify.error(err.data?.error || err.message || t("notify_delete_failed", "Failed to delete currency"));
    }
  };

  // A plain toggle would let two currencies both end up "base" with no
  // ordering guarantee — this is a dedicated action, not a field flip, so
  // the confirmation and loading state live here rather than in the form.
  const handleSetBase = async (row) => {
    try {
      setSettingBaseId(row.id);
      await setBaseCurrency(token, row.id);
      notify.success(t("notify_set_base_success", "{name} is now the base currency").replace("{name}", row.name));
      fetchCurrencies();
    } catch (err) {
      notify.error(err.data?.error || err.message || t("notify_set_base_failed", "Failed to set base currency"));
    } finally {
      setSettingBaseId(null);
    }
  };

  const columns = [
    { key: "id", label: tCommon("table_col_id", "ID"), className: "text-gray-400 font-mono" },
    {
      key: "name",
      label: t("col_name", "Currency"),
      render: (row) => (
        <div className="flex items-center gap-3 font-bold">
          <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
            <ICONS.Currencies className="w-4 h-4" />
          </div>
          <div>
            <div>{row.name}</div>
            <div className="text-[10px] text-gray-400 font-mono font-normal">{row.code} {row.symbol ? `· ${row.symbol}` : ""}</div>
          </div>
        </div>
      ),
    },
    {
      key: "exchange_rate",
      label: t("col_rate", "Rate vs Base"),
      className: "text-gray-700 font-mono",
      render: (row) => Number(row.exchange_rate || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 }),
    },
    { key: "decimal_places", label: t("col_decimals", "Decimals"), className: "text-gray-600" },
    {
      key: "is_base",
      label: t("col_base", "Base"),
      render: (row) =>
        row.is_base ? (
          <span className="px-2 py-1 rounded-full text-[10px] font-bold uppercase bg-blue-50 text-blue-600">{t("badge_base_currency", "Base Currency")}</span>
        ) : (
          <Button variant="outline" size="sm" onClick={() => handleSetBase(row)} loading={settingBaseId === row.id}>
            {t("btn_set_as_base", "Set as Base")}
          </Button>
        ),
    },
    {
      key: "is_active",
      label: tCommon("table_col_status", "Status"),
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${row.is_active ? "bg-green-50 text-green-600" : "bg-red-50 text-red-500"}`}>
          {row.is_active ? tCommon("status_active", "Active") : tCommon("status_inactive", "Inactive")}
        </span>
      ),
    },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        title={t("delete_confirm_title", "Delete Currency")}
        message={t("delete_confirm_message", "Are you sure? This will fail if the currency is the base currency or is still assigned to a branch.")}
        onConfirm={confirmDelete}
        onCancel={() => setModalConfig({ isOpen: false, id: null })}
        isLoading={status === "loading"}
        isSuccess={status === "success"}
        isError={status === "error"}
      />

      <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
            <ICONS.Currencies size={40} className="shrink-0" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>{t("page_title", "Currencies")}</h2>
            <p className="text-gray-400 text-xs md:text-sm font-medium">{t("page_subtitle", "Exactly one currency is the base — every other rate is measured against it")}</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
          <SearchInput
            value={searchTerm}
            onChange={(val) => { setSearchTerm(val); setCurrentPage(1); }}
            placeholder={t("search_placeholder", "Search currencies...")}
          />
          <Button variant="primary" icon={ICONS.Plus} onClick={() => navigate("/add-currency")} className="w-full sm:w-auto shrink-0">
            {t("btn_add_currency", "Add Currency")}
          </Button>
        </div>
      </PageHeader>

      <DataTable
        columns={columns}
        data={currentItems}
        sortConfig={sortConfig}
        onSort={requestSort}
        renderActions={(row, isMobile) => (
          <>
            <Button variant="outline" size="sm" icon={ICONS.Currencies} onClick={() => navigate(`/currencies/${row.id}/denominations`)}>{isMobile && ` ${t("btn_denominations", "Denominations")}`}</Button>
            <Button variant="outline" size="sm" icon={ICONS.EditUser} onClick={() => navigate(`/edit-currency/${row.id}`)}>{isMobile && ` ${tCommon("btn_edit", "Edit")}`}</Button>
            <Button variant="danger" size="sm" icon={ICONS.DeleteUser} onClick={() => handleDeleteClick(row.id)}>{isMobile && ` ${tCommon("btn_delete", "Delete")}`}</Button>
          </>
        )}
        mobileRender={(row) => (
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-gray-400 font-mono">{row.code}</span>
            <h3 className="font-bold text-gray-800">{row.name}</h3>
          </div>
        )}
        paginationConfig={{
          currentPage,
          totalPages,
          onPageChange: (p) => setCurrentPage(p),
          totalItems: sortedAndFiltered.length,
        }}
      />
    </div>
  );
}
